import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { api } from "./lib/api";
import { pruneDrafts } from "./lib/drafts";
import { useSession } from "./lib/session";
import type { ActionResult, VisitCard, VisitState } from "./lib/types";
import { Spinner } from "./components/bits";
import Login from "./screens/Login";
import Now from "./screens/Now";
import CardDetail from "./screens/CardDetail";
import VisitCardScreen from "./screens/VisitCard";
import Conquest from "./screens/Conquest";
import Places from "./screens/Places";
import PlaceArchive from "./screens/PlaceArchive";

// Leaflet and its CSS are a third of the bundle and are only needed on one screen.
// Splitting them out keeps the first load small, which matters on foreign data.
const MapScreen = lazy(() => import("./screens/MapScreen"));

const DEFAULT_HOLD_SIZE = 2;
const DEFAULT_PLACE_TYPE_TARGETS: Record<string, number> = {
  city: 100,
  town: 80,
  village: 60,
  hamlet: 40,
  other: 60,
};

export default function App() {
  const { user, loading } = useSession();

  if (loading) {
    return (
      <div className="screen" style={{ display: "grid", placeItems: "center", minHeight: "100dvh" }}>
        <Spinner label="Conquest wird geöffnet" />
      </div>
    );
  }

  if (!user) return <Login />;

  // Gameplay state lives below the session, keyed by user: signing out unmounts it
  // and the next account starts clean, without an effect resetting it by hand.
  return <SignedIn key={user.id} />;
}

function SignedIn() {
  const [visit, setVisit] = useState<VisitState | null>(null);
  const [conquest, setConquest] = useState<ActionResult | null>(null);
  // Whether the conquest also closed the visit. It normally does; a failed
  // request leaves it open, and the conquest screen must not claim otherwise.
  const [visitEnded, setVisitEnded] = useState(false);
  const [placeTypeTargets, setPlaceTypeTargets] = useState(DEFAULT_PLACE_TYPE_TARGETS);
  const [holdSize, setHoldSize] = useState(DEFAULT_HOLD_SIZE);
  const [visitCard, setVisitCard] = useState<VisitCard | null>(null);

  // The rules live on the server so the UI never hardcodes a tunable constant.
  useEffect(() => {
    api
      .rules()
      .then((rules) => {
        setPlaceTypeTargets(rules.place_type_targets ?? DEFAULT_PLACE_TYPE_TARGETS);
        setHoldSize(rules.hold_size);
        setVisitCard(rules.visit_card ?? null);
      })
      .catch(() => {
        setPlaceTypeTargets(DEFAULT_PLACE_TYPE_TARGETS);
        setHoldSize(DEFAULT_HOLD_SIZE);
      });
  }, []);

  // Sweep drafts for cards that have left play -- resolved, thrown away, or swept
  // up by a category veto. Guarded on a loaded visit: with none, "nothing is live"
  // is not a fact about the drafts, and clearing them would be wrong.
  useEffect(() => {
    if (!visit) return;
    void pruneDrafts([...visit.hand, ...visit.hold].map((card) => card.id));
  }, [visit]);

  const dismissConquest = useCallback(() => setConquest(null), []);

  return (
    <div className="app">
      <Routes>
        <Route path="/" element={<Now visit={visit} setVisit={setVisit} placeTypeTargets={placeTypeTargets} />} />
        <Route
          path="/card/:cardId"
          element={
            <CardDetail
              visit={visit}
              setVisit={setVisit}
              holdSize={holdSize}
              onConquest={(result, ended) => {
                setConquest(result);
                setVisitEnded(ended);
              }}
            />
          }
        />
        <Route
          path="/besuch"
          element={<VisitCardScreen visit={visit} setVisit={setVisit} card={visitCard} />}
        />
        <Route
          path="/conquest"
          element={<Conquest result={conquest} visitEnded={visitEnded} onDismiss={dismissConquest} />}
        />
        <Route
          path="/map"
          element={
            <Suspense
              fallback={
                <div className="screen screen--tabbed">
                  <Spinner label="Karte wird geladen" />
                </div>
              }
            >
              <MapScreen />
            </Suspense>
          }
        />
        <Route path="/places" element={<Places />} />
        <Route path="/places/:placeId" element={<PlaceArchive />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <TabBar />
    </div>
  );
}

function TabBar() {
  const { pathname } = useLocation();
  // The conquest screen owns the whole display; the card screen is a focused task.
  if (pathname === "/conquest" || pathname === "/besuch" || pathname.startsWith("/card/")) return null;

  return (
    <nav className="tabs" aria-label="Hauptnavigation">
      <NavLink to="/" className="tab" end>
        <FlagIcon />
        Jetzt
      </NavLink>
      <NavLink to="/map" className="tab">
        <GlobeIcon />
        Karte
      </NavLink>
      <NavLink to="/places" className="tab">
        <BookIcon />
        Orte
      </NavLink>
    </nav>
  );
}

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function FlagIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...stroke}>
      <path d="M5 21V4" />
      <path d="M5 4.5h11l-2.2 3.6L16 12H5" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...stroke}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.2 2.4 3.3 5.4 3.3 8.5s-1.1 6.1-3.3 8.5c-2.2-2.4-3.3-5.4-3.3-8.5S9.8 5.9 12 3.5Z" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...stroke}>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H19v14H5.5A1.5 1.5 0 0 0 4 19.5Z" />
      <path d="M4 19.5A1.5 1.5 0 0 1 5.5 18H19v2.5H5.5A1.5 1.5 0 0 1 4 19.5Z" />
    </svg>
  );
}
